import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * Transfer material between two bins at the same site (within-yard blend/move).
 * Props: source (stockpile), siblingBins (other bins at same site), onClose, onSaved.
 */
export default function TransferDialog({ source, siblingBins, onClose, onSaved }) {
  const [toBinId, setToBinId] = useState("");
  const [tons, setTons] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const dst = siblingBins.find((b) => b.id === toBinId);
  const tonsNum = parseFloat(tons) || 0;
  const srcAvailable = parseFloat(source.current_tons) || 0;
  const dstCap = dst ? parseFloat(dst.capacity_tons) || 0 : 0;
  const dstCur = dst ? parseFloat(dst.current_tons) || 0 : 0;

  const insufficientSrc = tonsNum > 0 && tonsNum > srcAvailable + 1e-6;
  const wouldOverflow = dst && dstCap > 0 && (dstCur + tonsNum) > dstCap;
  const overBy = wouldOverflow ? (dstCur + tonsNum - dstCap).toFixed(1) : 0;
  const blocked = !toBinId || tonsNum <= 0 || insufficientSrc || wouldOverflow;

  const submit = async (e) => {
    e.preventDefault();
    if (blocked) return;
    setBusy(true);
    try {
      await api.post(`/stockpiles/${source.id}/transfer`, {
        to_stockpile_id: toBinId,
        tons: tonsNum,
        reason: reason.trim(),
        notes: notes.trim(),
      });
      toast.success(`Transferred ${tonsNum}t ${source.bin_number} → ${dst.bin_number}`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white border border-gray-300 w-full max-w-md">
        <div className="border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="label-caps flex items-center gap-1.5 text-indigo-700"><ArrowRightLeft className="w-3 h-3" /> Transfer</div>
            <div className="font-display font-bold text-xl">Bin-to-Bin Transfer</div>
            <div className="text-xs text-gray-500 mt-0.5 font-mono">
              From <span className="font-bold text-gray-900">{source.bin_number}</span> · {srcAvailable.toFixed(1)}t available
            </div>
          </div>
          <button onClick={onClose} data-testid="close-transfer"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <F label="Destination Bin (same site)">
            <select
              required
              value={toBinId}
              onChange={(e) => setToBinId(e.target.value)}
              className="input-sharp"
              data-testid="transfer-to-select"
            >
              <option value="">Select…</option>
              {siblingBins.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.bin_number} · {b.name} · {b.classification} · {(b.current_tons ?? 0).toFixed(1)}/{b.capacity_tons}t
                </option>
              ))}
            </select>
            {siblingBins.length === 0 && (
              <div className="text-[11px] text-red-600 mt-1">No other bins at this site — create one first.</div>
            )}
            {dst && dstCap > 0 && (
              <div className="text-[11px] text-gray-500 mt-1 font-mono">
                Destination: {dstCur.toFixed(1)} / {dstCap}t{" · "}
                <span className={wouldOverflow ? "text-red-600 font-bold" : "text-emerald-700"}>
                  {wouldOverflow ? `over by ${overBy}t` : `room for ${(dstCap - dstCur).toFixed(1)}t`}
                </span>
              </div>
            )}
          </F>
          <F label="Tons">
            <input
              type="number"
              step="0.1"
              min="0.1"
              max={srcAvailable}
              required
              value={tons}
              onChange={(e) => setTons(e.target.value)}
              className="input-sharp font-mono"
              data-testid="transfer-tons-input"
              placeholder={`≤ ${srcAvailable.toFixed(1)}`}
            />
          </F>
          <F label="Reason (recommended, audit-logged)">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="input-sharp"
              data-testid="transfer-reason-input"
              placeholder="e.g. reblend after lab reclassification"
            />
          </F>
          <F label="Notes (optional)">
            <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="input-sharp" data-testid="transfer-notes-input" />
          </F>
          {insufficientSrc && (
            <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 p-2" data-testid="transfer-insufficient-warning">
              Source bin only has <span className="font-mono font-bold">{srcAvailable.toFixed(1)}t</span> available.
            </div>
          )}
          {wouldOverflow && (
            <div className="text-[11px] text-red-800 bg-red-50 border border-red-200 p-2" data-testid="transfer-capacity-warning">
              Destination bin <span className="font-mono font-bold">{dst.bin_number}</span> would overflow by <span className="font-mono font-bold">{overBy}t</span>.
            </div>
          )}
          <div className="text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 p-2">
            Both movements share a single <span className="font-mono">transfer_id</span> for audit correlation. Source gets an OUT movement (source: transfer_out), destination gets an IN (source: transfer_in).
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 py-2.5 text-xs uppercase tracking-widest font-semibold">Cancel</button>
            <button type="submit" disabled={busy || blocked || siblingBins.length === 0} data-testid="submit-transfer" className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 text-xs uppercase tracking-widest font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
              {busy ? "Transferring…" : "Transfer"}
            </button>
          </div>
        </form>
        <style>{`.input-sharp { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #D1D5DB; font-size: 0.875rem; background: white; } .input-sharp:focus { outline: none; border-color: #111827; box-shadow: 0 0 0 1px #111827; }`}</style>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="label-caps block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
